/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useRequest from '../hooks/useRequest';
import { useAuth } from './AuthContext';

interface RuntimeData {
    // Client data
    currentClientId: number | null;
    currentClientName: string | null;
    currentClientCNP: string | null;

    // Contract data
    currentContractNumber: string | null;
    currentContractStatus: string | null;
    currentContractValue: number | null;

    // Session data
    cashSessionId: number | null;
    cashSessionNumber: number | null;
    cashPointId: number | null;
    cashPointName: string | null;
    sessionStartTime: string | null;
    isNewSession: boolean | null;

    // User data
    userId: number | null;
    username: string | null;
    userRole: string | null;

    // System data
    serverTime: string | null;
    lastRefresh: string | null;

    // Cash data
    cashDrawerBalance: number | null;
}

interface DebugDataContextType {
    runtimeData: RuntimeData;
    isDebugWindowOpen: boolean;
    setIsDebugWindowOpen: (open: boolean) => void;
    updateClientData: (
        clientId: number | null,
        clientName: string | null,
        clientCNP?: string | null,
    ) => void;
    updateContractData: (
        contractNumber: string | null,
        status?: string | null,
        value?: number | null,
    ) => void;
    updateCashPointData: (cashPointId: number | null, cashPointName: string | null) => void;
    refreshSessionData: () => Promise<void>;
    initializeCashSession: (cashPointId: number) => Promise<void>;
    isRefreshing: boolean;
    isSessionInitialized: boolean;
}

const defaultRuntimeData: RuntimeData = {
    currentClientId: null,
    currentClientName: null,
    currentClientCNP: null,
    currentContractNumber: null,
    currentContractStatus: null,
    currentContractValue: null,
    cashSessionId: null,
    cashSessionNumber: null,
    cashPointId: null,
    cashPointName: null,
    sessionStartTime: null,
    isNewSession: null,
    userId: null,
    username: null,
    userRole: null,
    serverTime: null,
    lastRefresh: null,
    cashDrawerBalance: null,
};

const DebugDataContext = createContext<DebugDataContextType | undefined>(undefined);

interface DebugDataProviderProps {
    children: React.ReactNode;
}

export const DebugDataProvider = ({ children }: DebugDataProviderProps) => {
    const request = useRequest();
    const { user } = useAuth();
    const [runtimeData, setRuntimeData] = useState<RuntimeData>(defaultRuntimeData);
    const [isDebugWindowOpen, setIsDebugWindowOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isSessionInitialized, setIsSessionInitialized] = useState(false);

    // Update user data when auth changes
    useEffect(() => {
        if (user) {
            setRuntimeData(prev => ({
                ...prev,
                userId: user.id ? parseInt(user.id, 10) : null,
                username: user.username ?? null,
                userRole: user.role ?? null,
            }));
        } else {
            // User logged out - reset session state
            setIsSessionInitialized(false);
            setRuntimeData(defaultRuntimeData);
        }
    }, [user]);

    /**
     * Initialize cash session on application startup.
     * This gets or creates a cash session for the user and cashpoint.
     * If a previous session was not closed by a cash drawer handover, it uses that session.
     * Otherwise, it creates a new session.
     */
    const initializeCashSession = useCallback(
        async (cashPointId: number) => {
            if (isSessionInitialized) return;

            try {
                const sessionResponse = await request<{
                    sessionId: number;
                    sessionNumber: number;
                    cashPointId: number;
                    cashPointName: string;
                    userId: number;
                    startTime: string;
                    isNewSession: boolean;
                }>({
                    path: '/cash-drawer/session',
                    method: 'POST',
                    params: { cashPointId },
                });

                if (sessionResponse) {
                    setRuntimeData(prev => ({
                        ...prev,
                        cashSessionId: sessionResponse.sessionId,
                        cashSessionNumber: sessionResponse.sessionNumber,
                        cashPointId: sessionResponse.cashPointId,
                        cashPointName: sessionResponse.cashPointName || prev.cashPointName,
                        sessionStartTime: sessionResponse.startTime,
                        isNewSession: sessionResponse.isNewSession,
                    }));
                    setIsSessionInitialized(true);
                    console.log(
                        `Cash session ${sessionResponse.isNewSession ? 'created' : 'resumed'}: ` +
                            `ID=${sessionResponse.sessionId}, Number=${sessionResponse.sessionNumber}, ` +
                            `CashPoint=${sessionResponse.cashPointName}`,
                    );
                }
            } catch (error) {
                console.error('Failed to initialize cash session:', error);
            }
        },
        [request, isSessionInitialized],
    );

    const updateClientData = useCallback(
        (clientId: number | null, clientName: string | null, clientCNP: string | null = null) => {
            setRuntimeData(prev => ({
                ...prev,
                currentClientId: clientId,
                currentClientName: clientName,
                currentClientCNP: clientCNP,
            }));
        },
        [],
    );

    const updateContractData = useCallback(
        (
            contractNumber: string | null,
            status: string | null = null,
            value: number | null = null,
        ) => {
            setRuntimeData(prev => ({
                ...prev,
                currentContractNumber: contractNumber,
                currentContractStatus: status,
                currentContractValue: value,
            }));
        },
        [],
    );

    const updateCashPointData = useCallback(
        (cashPointId: number | null, cashPointName: string | null) => {
            setRuntimeData(prev => ({
                ...prev,
                cashPointId,
                cashPointName,
            }));
        },
        [],
    );

    const refreshSessionData = useCallback(async () => {
        if (isRefreshing) return;

        setIsRefreshing(true);
        try {
            const userId = user?.id ? parseInt(user.id, 10) : null;
            const currentCashPointId = runtimeData.cashPointId;

            // If no cashpoint is set yet, we cannot fetch meaningful data
            if (currentCashPointId === null) {
                setIsRefreshing(false);
                return;
            }

            // Fetch current session info (requires both userId and cashPointId)
            const sessionResponse =
                userId != null
                    ? await request<{
                          sessionId: number;
                          sessionNumber: number;
                          cashPointId: number;
                          cashPointName: string;
                          userId: number;
                          startTime: string;
                      } | null>({
                          path: `/cash-drawer/current-session/${userId}/${currentCashPointId}`,
                      })
                    : null;

            // Fetch cash drawer balance using the actual cashpoint ID
            const balanceResponse = await request<{ balance: number }>({
                path: `/cash-drawer/current-balance/${currentCashPointId}`,
            });

            // Note: /health endpoint is not under /api, so we use client time for debugging
            const currentTime = new Date().toISOString();

            setRuntimeData(prev => ({
                ...prev,
                cashSessionId: sessionResponse?.sessionId ?? prev.cashSessionId,
                cashSessionNumber: sessionResponse?.sessionNumber ?? prev.cashSessionNumber,
                cashPointId: sessionResponse?.cashPointId ?? prev.cashPointId,
                cashPointName: sessionResponse?.cashPointName ?? prev.cashPointName,
                sessionStartTime: sessionResponse?.startTime ?? prev.sessionStartTime,
                cashDrawerBalance: balanceResponse?.balance ?? null,
                serverTime: currentTime,
                lastRefresh: currentTime,
            }));
        } catch (error) {
            console.error('Failed to refresh debug data:', error);
            setRuntimeData(prev => ({
                ...prev,
                lastRefresh: new Date().toISOString(),
            }));
        } finally {
            setIsRefreshing(false);
        }
    }, [request, isRefreshing, user, runtimeData.cashPointId]);

    // Auto-refresh when debug window is open
    useEffect(() => {
        if (!isDebugWindowOpen) return;

        // Initial refresh
        refreshSessionData();

        // Set up interval for real-time refresh (every 2 seconds)
        const interval = setInterval(refreshSessionData, 2000);

        return () => clearInterval(interval);
    }, [isDebugWindowOpen, refreshSessionData]);

    const contextValue = useMemo(
        () => ({
            runtimeData,
            isDebugWindowOpen,
            setIsDebugWindowOpen,
            updateClientData,
            updateContractData,
            updateCashPointData,
            refreshSessionData,
            initializeCashSession,
            isRefreshing,
            isSessionInitialized,
        }),
        [
            runtimeData,
            isDebugWindowOpen,
            updateClientData,
            updateContractData,
            updateCashPointData,
            refreshSessionData,
            initializeCashSession,
            isRefreshing,
            isSessionInitialized,
        ],
    );

    return <DebugDataContext.Provider value={contextValue}>{children}</DebugDataContext.Provider>;
};

export const useDebugData = (): DebugDataContextType => {
    const context = useContext(DebugDataContext);
    if (context === undefined) {
        throw new Error('useDebugData must be used within a DebugDataProvider');
    }
    return context;
};

export default DebugDataContext;
